/**
 * LossReasonStatCards - Grid of stat cards showing lost loans by reason
 *
 * Displays:
 * - Total lost loans
 * - Breakdown by each reason category
 * - Click to filter by reason
 *
 * RETAINED dispositions (RETAINED_LOSS_REASONS, e.g. 'refinanced_in_house') are
 * NOT losses. They are left out of "Total Lost" and out of every percentage
 * denominator via splitLossMetrics, and appear as their own card only when a
 * loan actually carries one, so a tenant that never records one sees no change.
 */

import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import {
  LostLoansMetrics,
  LossReason,
  LOSS_REASON_LABELS,
  LOSS_REASON_COLORS,
  RETAINED_LOSS_REASONS,
  splitLossMetrics,
} from '../../hooks/useLostLoans';
import {
  AlertTriangle,
  Home,
  Building,
  HandCoins,
  Landmark,
  TrendingDown,
  Users,
  MapPin,
  Heart,
  HelpCircle,
  PhoneOff,
  Repeat,
} from 'lucide-react';

interface LossReasonStatCardsProps {
  metrics: LostLoansMetrics | null;
  selectedReason: LossReason | 'all';
  onReasonSelect: (reason: LossReason | 'all') => void;
  isLoading?: boolean;
}

// Icons for each reason
const REASON_ICONS: Record<LossReason, React.ReactNode> = {
  bridging_completed: <Building className="w-4 h-4" />,
  property_sold: <Home className="w-4 h-4" />,
  lost_to_competitor: <TrendingDown className="w-4 h-4" />,
  paid_off: <HandCoins className="w-4 h-4" />,
  debt_consolidation: <Landmark className="w-4 h-4" />,
  investment_sold: <Home className="w-4 h-4" />,
  relationship_settlement: <Users className="w-4 h-4" />,
  customer_moved: <MapPin className="w-4 h-4" />,
  deceased: <Heart className="w-4 h-4" />,
  uncontactable: <PhoneOff className="w-4 h-4" />,
  refinanced_in_house: <Repeat className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />,
};

// Short labels for compact display
const SHORT_LABELS: Record<LossReason, string> = {
  bridging_completed: 'Bridging',
  property_sold: 'Sold',
  lost_to_competitor: 'Competitor',
  paid_off: 'Paid Off',
  debt_consolidation: 'Consolidation',
  investment_sold: 'Investment',
  relationship_settlement: 'Settlement',
  customer_moved: 'Moved',
  deceased: 'Deceased',
  uncontactable: 'No Contact',
  refinanced_in_house: 'Retained',
  unknown: 'Unknown',
};

// Priority order for display (most important first). LOSSES ONLY: the retained
// dispositions are rendered separately below and must never enter this list,
// because every card in it shows a share of total losses.
const DISPLAY_ORDER: LossReason[] = [
  'lost_to_competitor',
  'property_sold',
  'bridging_completed',
  'paid_off',
  'unknown',
  'debt_consolidation',
  'investment_sold',
  'relationship_settlement',
  'customer_moved',
  'deceased',
  'uncontactable',
];

const LossReasonStatCards: React.FC<LossReasonStatCardsProps> = ({
  metrics,
  selectedReason,
  onReasonSelect,
  isLoading = false,
}) => {
  if (isLoading || !metrics) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {/* Total card skeleton */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-16 mb-3" />
          <div className="h-8 bg-gray-700 rounded w-12 mb-2" />
          <div className="h-3 bg-gray-700 rounded w-20" />
        </div>
        {/* Reason card skeletons */}
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-gray-800 rounded-lg p-3 border border-gray-700 animate-pulse">
            <div className="h-3 bg-gray-700 rounded w-14 mb-2" />
            <div className="h-6 bg-gray-700 rounded w-8" />
          </div>
        ))}
      </div>
    );
  }

  // Filter to only show reasons with counts > 0, plus always show top 5
  const topReasons = DISPLAY_ORDER.slice(0, 5);
  const reasonsWithCounts = DISPLAY_ORDER.filter(
    reason => (metrics.byReason[reason] || 0) > 0 || topReasons.includes(reason)
  ).slice(0, 8); // Max 8 reason cards

  // Losses and retained are separate populations: "Total Lost" and every
  // percentage below use the LOST figures only.
  const { lostTotal, lostValueCents } = splitLossMetrics(metrics);
  const retainedWithCounts = RETAINED_LOSS_REASONS.filter(
    (reason) => (metrics.byReason[reason] || 0) > 0,
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {/* Total Lost Card */}
        <button
          onClick={() => onReasonSelect('all')}
          className={`bg-gray-800 rounded-lg p-4 border text-left transition-all hover:bg-gray-750 ${
            selectedReason === 'all'
              ? 'border-red-500 ring-1 ring-red-500/50'
              : 'border-gray-700 hover:border-gray-600'
          }`}
        >
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span>Total Lost</span>
          </div>
          <p className="text-2xl font-bold text-white">{lostTotal.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">
            {formatCurrency(lostValueCents)} value
          </p>
        </button>

        {/* Reason Cards */}
        {reasonsWithCounts.map((reason) => {
          const count = metrics.byReason[reason] || 0;
          const value = metrics.byReasonValueCents[reason] || 0;
          const colors = LOSS_REASON_COLORS[reason];
          const isSelected = selectedReason === reason;
          const percentage = lostTotal > 0 ? ((count / lostTotal) * 100).toFixed(0) : '0';

          return (
            <button
              key={reason}
              onClick={() => onReasonSelect(reason)}
              className={`bg-gray-800 rounded-lg p-3 border text-left transition-all hover:bg-gray-750 ${
                isSelected
                  ? `${colors.border} ring-1 ring-opacity-50`
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-1.5 text-xs mb-1.5">
                <span className={colors.text}>{REASON_ICONS[reason]}</span>
                <span className="text-gray-400 truncate">{SHORT_LABELS[reason]}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <p className={`text-xl font-bold ${count > 0 ? colors.text : 'text-gray-500'}`}>
                  {count.toLocaleString()}
                </p>
                {count > 0 && (
                  <span className="text-xs text-gray-500">{percentage}%</span>
                )}
              </div>
              {count > 0 && value > 0 && (
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {formatCurrency(value)}
                </p>
              )}
            </button>
          );
        })}

        {/* Retained cards: not losses, so no share-of-losses percentage. */}
        {retainedWithCounts.map((reason) => {
          const count = metrics.byReason[reason] || 0;
          const value = metrics.byReasonValueCents[reason] || 0;
          const colors = LOSS_REASON_COLORS[reason];
          const isSelected = selectedReason === reason;
          return (
            <button
              key={reason}
              onClick={() => onReasonSelect(reason)}
              title={`${LOSS_REASON_LABELS[reason]}: retained, not counted in Total Lost`}
              className={`bg-gray-800 rounded-lg p-3 border text-left transition-all hover:bg-gray-750 ${
                isSelected
                  ? `${colors.border} ring-1 ring-opacity-50`
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-1.5 text-xs mb-1.5">
                <span className={colors.text}>{REASON_ICONS[reason]}</span>
                <span className="text-gray-400 truncate">{SHORT_LABELS[reason]}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <p className={`text-xl font-bold ${colors.text}`}>{count.toLocaleString()}</p>
                <span className="text-xs text-gray-500">not a loss</span>
              </div>
              {value > 0 && (
                <p className="text-xs text-gray-500 mt-0.5 truncate">{formatCurrency(value)}</p>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected filter indicator */}
      {selectedReason !== 'all' && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Filtering by:</span>
          <span className={`px-2 py-0.5 rounded ${LOSS_REASON_COLORS[selectedReason].bg} ${LOSS_REASON_COLORS[selectedReason].text} ${LOSS_REASON_COLORS[selectedReason].border} border`}>
            {LOSS_REASON_LABELS[selectedReason]}
          </span>
          <button
            onClick={() => onReasonSelect('all')}
            className="text-gray-500 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default LossReasonStatCards;
